import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import RegistrationModal from "@/components/RegistrationModal";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Anchor, HelpCircle } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";

export default function Planning() {
  const [days, setDays] = useState([]);
  const [activeDayId, setActiveDayId] = useState(null);
  const [displayMode, setDisplayMode] = useState("tabs"); // "tabs" | "stacked"
  const [pageTitle, setPageTitle] = useState("Planning des bénévoles");
  const [pageSubtitle, setPageSubtitle] = useState("");
  const [planningByDay, setPlanningByDay] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const sectionRefs = useRef({});

  const loadPlanning = useCallback(async (dayId) => {
    const { data } = await api.get(`/planning/${dayId}`);
    setPlanningByDay((prev) => ({ ...prev, [dayId]: data }));
    return data;
  }, []);

  // Initial load
  useEffect(() => {
    (async () => {
      const [{ data: ds }, { data: settings }] = await Promise.all([
        api.get("/days"),
        api.get("/settings"),
      ]);
      setDays(ds);
      setDisplayMode(settings.display_mode || "tabs");
      setPageTitle(settings.page_title || "Planning des bénévoles");
      setPageSubtitle(settings.page_subtitle || "");
      if (settings.display_mode === "stacked") {
        await Promise.all(ds.map((d) => loadPlanning(d.id)));
        if (ds.length > 0) setActiveDayId(ds[0].id);
      } else if (ds.length > 0) {
        setActiveDayId(ds[0].id);
        await loadPlanning(ds[0].id);
      }
      setLoading(false);
    })();
  }, [loadPlanning]);

  // When activeDayId changes in tabs mode, load that day if not yet loaded
  useEffect(() => {
    if (displayMode === "tabs" && activeDayId && !planningByDay[activeDayId]) {
      loadPlanning(activeDayId);
    }
  }, [activeDayId, displayMode, planningByDay, loadPlanning]);

  const onRegistered = async () => {
    const dayId = selectedSlot?.dayId;
    setSelectedSlot(null);
    if (displayMode === "stacked") {
      await Promise.all(days.map((d) => loadPlanning(d.id)));
    } else if (dayId) {
      await loadPlanning(dayId);
    }
  };

  const scrollToDay = (dayId) => {
    setActiveDayId(dayId);
    const el = sectionRefs.current[dayId];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="planning-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0b3a73] tracking-tight">
            {pageTitle}
          </h1>
          {pageSubtitle && (
            <p className="text-sm text-slate-600 mt-2 max-w-2xl whitespace-pre-line">
              {pageSubtitle}
            </p>
          )}
        </div>
        {days.length > 1 && (
          <div className="flex gap-2 flex-wrap" data-testid="day-nav">
            {days.map((d) => (
              <button
                key={d.id}
                data-testid={`day-tab-${d.id}`}
                onClick={() =>
                  displayMode === "stacked" ? scrollToDay(d.id) : setActiveDayId(d.id)
                }
                className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                  activeDayId === d.id
                    ? "bg-[#0b3a73] text-white border-[#0b3a73]"
                    : "bg-white text-slate-700 border-slate-300 hover:border-[#0b3a73]"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : displayMode === "stacked" ? (
        <div className="space-y-12">
          {days.map((d) => (
            <section
              key={d.id}
              id={`day-${d.id}`}
              ref={(el) => (sectionRefs.current[d.id] = el)}
              data-testid={`day-section-${d.id}`}
              className="scroll-mt-24"
            >
              <DayHeader day={d} />
              {planningByDay[d.id] ? (
                <>
                  <PlanningTable
                    planning={planningByDay[d.id]}
                    onSlotClick={(slot, area, ts) =>
                      setSelectedSlot({ slot, area, ts, dayId: d.id })
                    }
                  />
                  <DayNote html={planningByDay[d.id].day?.note} />
                </>
              ) : (
                <Skeleton className="h-32 w-full" />
              )}
            </section>
          ))}
        </div>
      ) : (
        activeDayId &&
        planningByDay[activeDayId] && (
          <div className="space-y-3">
            <DayHeader day={planningByDay[activeDayId].day} />
            <PlanningTable
              planning={planningByDay[activeDayId]}
              onSlotClick={(slot, area, ts) =>
                setSelectedSlot({ slot, area, ts, dayId: activeDayId })
              }
            />
            <DayNote html={planningByDay[activeDayId].day?.note} />
          </div>
        )
      )}

      <RegistrationModal
        open={!!selectedSlot}
        onOpenChange={(v) => !v && setSelectedSlot(null)}
        context={selectedSlot}
        onSuccess={onRegistered}
      />
    </div>
  );
}

function DayHeader({ day }) {
  if (!day) return null;
  return (
    <div className="flex items-center gap-2 text-[#0b3a73] mb-3">
      <Anchor className="w-6 h-6" />
      <span className="font-serif text-2xl sm:text-3xl">{day.label}</span>
    </div>
  );
}

function DayNote({ html }) {
  if (!html) return null;
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  if (!stripped) return null;
  const safe = sanitizeHtml(html);
  if (!safe) return null;
  return (
    <div
      className="rich-content mt-4 px-1"
      data-testid="day-note"
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

function PlanningTable({ planning, onSlotClick }) {
  const slotsFor = (areaId, tsId) =>
    planning.slots
      .filter((s) => s.area_id === areaId && s.timeslot_id === tsId)
      .sort((a, b) => a.position - b.position);

  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="planning-table-wrapper">
      <table className="w-full border-collapse min-w-[760px]" data-testid="planning-table">
        <thead>
          <tr>
            <th className="bg-[#0b3a73] text-white text-left px-4 py-3 font-medium text-sm uppercase tracking-wide w-48">
              Zone
            </th>
            {planning.timeslots.map((ts) => (
              <th
                key={ts.id}
                className="bg-[#0b3a73] text-white px-3 py-3 font-medium text-sm text-center border-l border-[#0a2f5c]"
                data-testid={`ts-header-${ts.id}`}
              >
                {ts.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {planning.areas.map((area) => (
            <tr key={area.id} className="border-t-2 border-[#0b3a73]">
              <th
                scope="row"
                className="bg-[#cfe2f3] text-[#0b3a73] text-left px-4 py-3 font-medium text-sm align-top"
                data-testid={`area-header-${area.id}`}
              >
                <div className="flex items-start gap-1.5">
                  <span className="flex-1">{area.name}</span>
                  {area.description && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className="shrink-0 mt-0.5 text-[#0b3a73]/70 hover:text-[#0b3a73] transition-colors"
                          data-testid={`area-help-${area.id}`}
                          title="En savoir plus"
                          aria-label={`Description de ${area.name}`}
                        >
                          <HelpCircle className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="right"
                        align="start"
                        className="w-64 text-sm text-slate-700 leading-relaxed"
                        data-testid={`area-help-popover-${area.id}`}
                      >
                        <div className="font-medium text-[#0b3a73] mb-1">{area.name}</div>
                        <div className="whitespace-pre-line">{area.description}</div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </th>
              {planning.timeslots.map((ts) => {
                const cellSlots = slotsFor(area.id, ts.id);
                return (
                  <td
                    key={ts.id}
                    className="border-l border-slate-200 align-top p-0"
                    data-testid={`cell-${area.id}-${ts.id}`}
                  >
                    <div className="flex flex-col">
                      {cellSlots.map((slot) => {
                        if (slot.blocked) {
                          return (
                            <div
                              key={slot.id}
                              className="bg-black text-black h-12 border-b border-slate-700 last:border-b-0"
                              data-testid={`slot-blocked-${slot.id}`}
                              aria-label="Créneau indisponible"
                            />
                          );
                        }
                        if (slot.registration) {
                          return (
                            <div
                              key={slot.id}
                              className="bg-white text-slate-800 h-12 px-3 flex items-center text-sm border-b border-slate-200 last:border-b-0 font-medium"
                              data-testid={`slot-taken-${slot.id}`}
                            >
                              {slot.registration.first_name} {slot.registration.last_name}
                            </div>
                          );
                        }
                        return (
                          <button
                            key={slot.id}
                            onClick={() => onSlotClick(slot, area, ts)}
                            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-800 h-12 px-3 flex items-center justify-center text-sm font-medium border-b border-slate-200 last:border-b-0 transition-colors cursor-pointer"
                            data-testid={`slot-available-${slot.id}`}
                          >
                            S'inscrire
                          </button>
                        );
                      })}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
